import type { Cell } from "../src/protocol";
import { h, svg } from "./dom";
import { sameCell, snapToLine } from "./geometry";
import { STRINGS } from "./strings";

export interface BoardCallbacks {
  /** Pointer released on a line of 2+ cells. */
  onSelect(start: Cell, end: Cell): void;
  /** The selected line changed while dragging. */
  onDrag?(start: Cell, end: Cell): void;
  /** The drag finished (released or cancelled). */
  onDragEnd?(): void;
}

export interface BoardLine {
  start: Cell;
  end: Cell;
  color: string;
}

export interface FoundLine extends BoardLine {
  word: string;
}

/** How long we wait for the server's verdict before dropping the pending selection. */
const PENDING_TIMEOUT_MS = 2000;
const MISS_ANIMATION_MS = 450;
/** A remote preview that gets no update for this long is assumed stale (lost drag_end). */
const PREVIEW_EXPIRY_MS = 10_000;

/**
 * Letter grid (CSS Grid) with an SVG layer underneath for lines: found words, other
 * players' live previews and the player's own selection. Uses Pointer Events so mouse
 * and touch share one code path.
 */
export class BoardView {
  readonly el: HTMLElement;
  private readonly gridEl: HTMLElement;
  private readonly overlay: SVGSVGElement;
  private readonly foundLayer: SVGGElement;
  private readonly previewLayer: SVGGElement;
  private readonly selectionLine: SVGLineElement;
  /** Letters under the current drag, shown away from the finger (which covers them). */
  private readonly selectionLabel: HTMLElement;
  private grid: string[][] = [];
  private size = 0;
  private color = "currentColor";
  private enabled = true;
  private drag: { pointerId: number; start: Cell; end: Cell } | null = null;
  private pendingTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly previews = new Map<string, { el: SVGLineElement; timer: ReturnType<typeof setTimeout> }>();

  constructor(private readonly callbacks: BoardCallbacks) {
    this.overlay = svg("svg", { class: "board-lines", "aria-hidden": "true" });
    this.foundLayer = svg("g");
    this.previewLayer = svg("g");
    this.selectionLine = svg("line", { class: "line line-selection" });
    this.overlay.append(this.foundLayer, this.previewLayer, this.selectionLine);
    this.gridEl = h("div", { class: "board-grid", role: "grid", "aria-label": STRINGS.boardLabel });
    this.selectionLabel = h("div", { class: "selection-label", "aria-hidden": "true", hidden: true });
    this.el = h("div", { class: "board" }, this.overlay, this.gridEl, this.selectionLabel);

    this.gridEl.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    this.gridEl.addEventListener("pointermove", (e) => this.onPointerMove(e));
    this.gridEl.addEventListener("pointerup", (e) => this.onPointerUp(e));
    this.gridEl.addEventListener("pointercancel", (e) => this.onPointerCancel(e));
  }

  /** `animate` deals the new letters in with a short diagonal wave. */
  setGrid(grid: string[][], animate = false): void {
    this.grid = grid;
    this.size = grid.length;
    this.el.style.setProperty("--size", String(this.size));
    this.overlay.setAttribute("viewBox", `0 0 ${this.size} ${this.size}`);
    this.gridEl.replaceChildren(
      ...grid.map((row, r) =>
        h(
          "div",
          { class: "board-row", role: "row" },
          ...row.map((letter, c) => {
            const cell = h("span", { class: animate ? "cell cell-deal" : "cell", role: "gridcell" }, letter);
            if (animate) cell.style.setProperty("--wave", String(r + c));
            return cell;
          }),
        ),
      ),
    );
    this.cancelDrag();
    this.clearPending();
    this.clearPreviews();
  }

  setPlayerColor(color: string): void {
    this.color = color;
    this.selectionLine.style.stroke = color;
  }

  /**
   * play: interactive. offline: ignores input and is dimmed (reconnecting).
   * locked: ignores input but stays fully readable (round finished).
   */
  setMode(mode: "play" | "offline" | "locked"): void {
    this.enabled = mode === "play";
    this.el.classList.toggle("board-disabled", mode === "offline");
    this.el.classList.toggle("board-locked", mode === "locked");
    if (!this.enabled) {
      this.cancelDrag();
      this.clearPending();
    }
  }

  /**
   * Draws all found words. `animate` names a word that was just found, which gets an
   * entrance animation (`mine` makes it the stronger "pop" used for the player's own finds).
   */
  setFound(lines: FoundLine[], animate?: { word: string; mine: boolean }): void {
    this.foundLayer.replaceChildren(
      ...lines.map((line) => {
        const el = svg("line", { class: "line line-found" });
        placeLine(el, line.start, line.end);
        el.style.stroke = line.color;
        if (animate?.word === line.word) el.classList.add(animate.mine ? "line-pop" : "line-appear");
        return el;
      }),
    );
  }

  /** Shows (or with `null`, hides) another player's in-progress selection. */
  setPreview(playerId: string, line: BoardLine | null): void {
    const existing = this.previews.get(playerId);
    if (!line) {
      if (existing) {
        clearTimeout(existing.timer);
        existing.el.remove();
        this.previews.delete(playerId);
      }
      return;
    }
    const el = existing?.el ?? svg("line", { class: "line line-preview" });
    placeLine(el, line.start, line.end);
    el.style.stroke = line.color;
    if (!existing) this.previewLayer.append(el);
    if (existing) clearTimeout(existing.timer);
    const timer = setTimeout(() => this.setPreview(playerId, null), PREVIEW_EXPIRY_MS);
    this.previews.set(playerId, { el, timer });
  }

  clearPreviews(): void {
    for (const id of [...this.previews.keys()]) this.setPreview(id, null);
  }

  /** The server rejected the last selection: shake it, then fade it out. */
  rejectPending(): void {
    if (!this.selectionLine.classList.contains("line-pending")) return;
    clearTimeout(this.pendingTimer);
    this.selectionLine.classList.remove("line-miss");
    // Force a reflow so the animation restarts even if it ran moments ago.
    void this.overlay.getBoundingClientRect();
    this.selectionLine.classList.add("line-miss");
    this.pendingTimer = setTimeout(() => this.clearPending(), MISS_ANIMATION_MS);
  }

  /** The server accepted the last selection: the found line replaces it. */
  acceptPending(): void {
    this.clearPending();
  }

  private onPointerDown(e: PointerEvent): void {
    if (!this.enabled || this.drag || this.size === 0) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const point = this.pointToCell(e);
    if (!point) return;
    e.preventDefault();
    this.clearPending();
    this.gridEl.setPointerCapture(e.pointerId);
    const start: Cell = [Math.floor(point[0]), Math.floor(point[1])];
    this.drag = { pointerId: e.pointerId, start, end: start };
    this.drawSelection();
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.drag || e.pointerId !== this.drag.pointerId) return;
    const point = this.pointToCell(e, true);
    if (!point) return;
    const end = snapToLine(this.drag.start, point, this.size);
    if (sameCell(end, this.drag.end)) return;
    this.drag.end = end;
    this.drawSelection();
    this.callbacks.onDrag?.(this.drag.start, end);
  }

  private onPointerUp(e: PointerEvent): void {
    if (!this.drag || e.pointerId !== this.drag.pointerId) return;
    const { start, end } = this.drag;
    this.drag = null;
    this.selectionLabel.hidden = true;
    this.callbacks.onDragEnd?.();
    if (sameCell(start, end)) {
      this.hideSelection();
      return;
    }
    // Keep the line on screen until the server answers with found or miss.
    this.selectionLine.classList.add("line-pending");
    this.pendingTimer = setTimeout(() => this.clearPending(), PENDING_TIMEOUT_MS);
    this.callbacks.onSelect(start, end);
  }

  private onPointerCancel(e: PointerEvent): void {
    if (this.drag && e.pointerId === this.drag.pointerId) this.cancelDrag();
  }

  private cancelDrag(): void {
    if (!this.drag) return;
    this.drag = null;
    this.hideSelection();
    this.callbacks.onDragEnd?.();
  }

  private clearPending(): void {
    clearTimeout(this.pendingTimer);
    this.pendingTimer = undefined;
    if (!this.selectionLine.classList.contains("line-pending")) return;
    this.selectionLine.classList.remove("line-pending", "line-miss");
    if (!this.drag) this.hideSelection();
  }

  private drawSelection(): void {
    if (!this.drag) return;
    const { start, end } = this.drag;
    placeLine(this.selectionLine, start, end);
    this.selectionLine.style.stroke = this.color;
    this.selectionLine.style.visibility = "visible";

    this.selectionLabel.textContent = this.lettersBetween(start, end);
    this.selectionLabel.style.setProperty("--sel", this.color);
    // The finger sits near `end`: put the label in the other half of the board.
    this.selectionLabel.classList.toggle("selection-label-bottom", end[0] < this.size / 2);
    this.selectionLabel.hidden = false;
  }

  private hideSelection(): void {
    this.selectionLine.style.visibility = "hidden";
    this.selectionLabel.hidden = true;
  }

  private lettersBetween(start: Cell, end: Cell): string {
    const dr = Math.sign(end[0] - start[0]);
    const dc = Math.sign(end[1] - start[1]);
    const len = Math.max(Math.abs(end[0] - start[0]), Math.abs(end[1] - start[1])) + 1;
    let letters = "";
    for (let k = 0; k < len; k++) letters += this.grid[start[0] + dr * k]?.[start[1] + dc * k] ?? "";
    return letters;
  }

  /** Pointer position in fractional cell units; null when outside the board (unless clamped). */
  private pointToCell(e: PointerEvent, clamp = false): [number, number] | null {
    const rect = this.gridEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    let row = ((e.clientY - rect.top) / rect.height) * this.size;
    let col = ((e.clientX - rect.left) / rect.width) * this.size;
    const inside = row >= 0 && row < this.size && col >= 0 && col < this.size;
    if (!inside && !clamp) return null;
    const max = this.size - 1e-6;
    row = Math.min(Math.max(row, 0), max);
    col = Math.min(Math.max(col, 0), max);
    return [row, col];
  }
}

function placeLine(el: SVGLineElement, start: Cell, end: Cell): void {
  el.setAttribute("x1", String(start[1] + 0.5));
  el.setAttribute("y1", String(start[0] + 0.5));
  el.setAttribute("x2", String(end[1] + 0.5));
  el.setAttribute("y2", String(end[0] + 0.5));
}
